import Vue from 'vue';
import _ from 'lodash';
import moment from 'moment-timezone';
import { Room, RoomEvent, Track, createLocalTracks } from 'livekit-client';

import EventBus from '../../event-bus';
import callHungUpSoundSrc from '@/assets/sound/call_hangup.mp3';
import callReceivedSoundSrc from '@/assets/sound/call_answered.mp3';
import { CallRecorder } from '@/services/callRecording/recorder';

const callHungUpSound   = new Audio(callHungUpSoundSrc);
const callReceivedSound = new Audio(callReceivedSoundSrc);

// Grabador de backup de la llamada manual en curso (solo llamadas manuales).
// Vive a nivel de módulo porque MediaRecorder/AudioContext no van en el state.
let recorder = null;

export default {
  async getSipAuth() {
    const defaultMessage = 'Ocurrio un error obteniendo el token, intente de nuevamente.';
    return Vue.axios.post('/call_center/token', { conn_type: 'manual_call' })
      .then(({ data }) => ({
          err: null,
        data,
      }))
      .catch(({ response: { data: { message = defaultMessage } = { message: defaultMessage } } = { data: { message: defaultMessage } } }) => ({
        err: message,
        data: null,
      }));
  },
  hangupCall({ state, rootState, dispatch }) {
    if (!state.room) {
      return;
    }

    const notification = rootState.dialer.notification;
    if (Number(notification.company_id) === 86496225) {
      const nsId = notification.id || null;
      const callUuid = notification.call_uuid || null;
      const pending = rootState.dialer.callsToSave.some(function(item) {
        return item.id === nsId || item.call_uuid === callUuid;
      });
      if (pending) {
        EventBus.$emit('modal', 'Debe guardar el estatus antes de colgar la llamada');
        return;
      }
    }

    return Vue.axios.post('/call_center/livekit/hangup')
    .then(() => dispatch('performDisconnection'))
    .catch(({ response }) => {
      console.log('ERR COLGANDO LA LLAMADA', response);
    });
  },
  muteCall({ state }) {
    if (!state.room) {
      return;
    }

    if (state.audioTrack.isMuted) {
      return state.audioTrack.unmute();
    }

    return state.audioTrack.mute();
  },
  async performDisconnection({ state }) {
    // Detener la grabación de backup (idempotente) antes de cerrar la sala.
    if (recorder) {
      await recorder.stop();
    }
    if (!state.room) {
      return;
    }
    return state.room.disconnect();
  },
  postDisconnection({ commit }) {
    recorder = null;
    commit('SET_ROOM', null);
    commit('SET_AUDIO_TRACK', null);
    commit('SET_RECONNECTING', false);
    commit('dialer/SET_CALL_TYPE', '', { root: true });
    commit('dialer/SET_NOTIFICATION', {}, { root: true });
    commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', 0, { root: true });
  },
  async createManualCall({ commit }, body) {
    const call = await Vue.axios.post('customers/call_v2', {
      customer_id: body.customer_id,
      phone: body.phone,
    })
    .then((response) => ({ success: true, data: response.data }))
    .catch((error) => ({ success: false, error }));

    if (!call.success) {
      // La API explica el motivo (sin proveedor manual para el país, usuario ya
      // en llamada, etc.). Mostrar ese mensaje en lugar del genérico.
      const apiMessage = (((call.error || {}).response || {}).data || {}).message;
      EventBus.$emit('modal', apiMessage || 'Error, al intentar hacer la llamada, intente nuevamente.');
      return false;
    }

    if (call.data.code && call.data.message) {
      EventBus.$emit('modal', call.data.message);
      return false;
    }

    commit('dialer/SET_NOTIFICATION', {
      ...call.data,
      name: `${call.data.name || call.data.first_name}${!call.data.name && call.data.last_name ? ` ${call.data.last_name}` : ''}`,
      phone: call.data.to,
    }, { root: true });
    commit('dialer/ADD_CALL_TO_SAVE', call.data, { root: true });
    return true;
  },
  async startLiveKit({ state, rootState, commit, dispatch }, data) {
    commit('dialer/SET_CALL_TYPE', 'manual_v2', { root: true });

    const { err, data: auth } = await dispatch('getSipAuth');
    if (err) {
      commit('dialer/SET_CALL_TYPE', '', { root: true });
      EventBus.$emit('modal', err);
      throw Error(err);
    }

    const [audioTrack] = await createLocalTracks({
      audio: true,
      video: false,
    });

    // Backup de la grabación en el navegador (solo llamadas manuales).
    // Precondición dura: si este navegador no puede grabar el backup, la
    // llamada NO se coloca.
    recorder = new CallRecorder();
    const canRecord = await recorder.preflight();
    if (!canRecord) {
      audioTrack.stop();
      recorder = null;
      commit('dialer/SET_CALL_TYPE', '', { root: true });
      EventBus.$emit('modal', 'No se puede iniciar la llamada: la grabación de respaldo no está disponible en este navegador. Revisa los permisos de almacenamiento e inténtalo nuevamente.');
      throw Error('backup recording preflight failed');
    }

    const { server, token, roomName } = auth;
    const room = new Room(roomName);

    // Arranca la grabación de backup UNA sola vez, cuando llega el audio del
    // cliente (TrackSubscribed). Ese es el único momento con sentido: solo ahí
    // existe el track remoto que hace útil la grabación. NO se usa
    // ParticipantConnected (no siempre dispara, y si lo hiciera antes del track
    // grabaría una sola vía). El flag síncrono evita doble arranque si
    // TrackSubscribed llega más de una vez.
    let recordingBegun = false;
    const beginRecording = () => {
      if (recordingBegun || !recorder) {
        return;
      }
      recordingBegun = true;
      const notification = rootState.dialer.notification || {};
      recorder.start({
        localTrack: audioTrack.mediaStreamTrack,
        meta: {
          call_uuid: notification.call_uuid,
          company_id: notification.company_id,
          recorded_at: moment.utc().toISOString(),
          direction: 'outbound',
        },
      }).catch((e) => {
        console.error('CallRecorder: fallo al iniciar la grabación', e);
      });
    };

    // Conexion establecida
    room.on(RoomEvent.SignalConnected, async () => {
      console.log('SIGNAL_CONNECTED');

      // room.localParticipant.publishTrack(audioTrack).then(() => audioTrack.mute());
      room.localParticipant.publishTrack(audioTrack);

      commit('SET_ROOM', room);
      commit('SET_AUDIO_TRACK', audioTrack);

      const call = await dispatch('createManualCall', data);
      if (!call) {
        return dispatch('performDisconnection');
      }
    });

    // Eventos de audio
    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind !== Track.Kind.Audio) {
        console.log('TRACK', track.kind, 'NOT SUPPORTED');
        return;
      }

      const audioElement = document.getElementById('callCenterAudio');
      const element = track.attach();
      audioElement.appendChild(element);

      // Alimentar la voz del cliente al grabador y arrancar la grabación:
      // hay audio remoto => el cliente contestó. Trigger principal.
      if (recorder) {
        recorder.setRemoteTrack(track.mediaStreamTrack);
        beginRecording();
      }
    });
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach();
    });

    // Eventos de participantes
    room.on(RoomEvent.ParticipantConnected, () => {
      console.log('PARTICIPANT_CONNECTED');

      commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', moment.utc().unix(), { root: true });

      audioTrack.unmute();
      callReceivedSound.play();
    });
    room.on(RoomEvent.ParticipantDisconnected, () => {
      audioTrack.mute();
      callHungUpSound.play();

      return dispatch('performDisconnection');
    });

    // Eventos de reconexión
    room.on(RoomEvent.SignalReconnecting, () => {
      console.log('SIGNAL_RECONNECTING');
      if (state.reconnecting) {
        return;
      }

      commit('SET_RECONNECTING', true);
    });
    room.on(RoomEvent.Disconnected, () => {
      console.log('DISCONNECTED');
      if (state.reconnecting) {
        EventBus.$emit('modal', 'Se perdio la conexión con el servidor de llamadas');
      }

      return dispatch('postDisconnection');
    });
    room.on(RoomEvent.Reconnected, () => {
      console.log('RECONNECTED');
      commit('SET_RECONNECTING', false);
    });

    // Force TURN
    const connectOptions = {
      rtcConfig: {
        iceTransportPolicy: 'relay',
      },
    };

    const result = await room.connect(server, token, connectOptions)
      .then(() => ({ success: true }))
      .catch(err => ({ err }));

    if (result.err) {
      throw Error(result.err);
    }
  },
};



// WEBPACK FOOTER //
// ./src/store/manual_calls/actions.js